import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  TrendUp,
  TrendDown,
  Percent,
  X,
  WarningCircle,
  Sparkle,
  Clock,
  ArrowUDownLeft,
  ChartLine,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { formatINR, formatTenure, emi as emiFn } from "../../lib/loanMath";

const REASONS = [
  { v: "repo_change", label: "RBI repo rate change" },
  { v: "mclr_reset", label: "Bank MCLR/EBLR reset" },
  { v: "spread_change", label: "Spread change" },
  { v: "fixed_revision", label: "Fixed rate revision" },
  { v: "balance_transfer", label: "Rate negotiated / balance transfer" },
  { v: "other", label: "Other" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * RateChangeDialog — Feature 4 single-loan rate update.
 *
 * Props:
 *   open, onClose, loan (one normalised row), planName,
 *   onApplied({ loan_name, new_rate, new_emi, new_tenure_months }),
 *   onPreparePrepayment({ loan_name, target_months }) — optional
 */
export default function RateChangeDialog({
  open, onClose, loan, planName, onApplied, onPreparePrepayment,
}) {
  const [mode, setMode] = useState("rate");            // "rate" | "bps"
  const [newRate, setNewRate] = useState("");
  const [deltaBps, setDeltaBps] = useState(0);
  const [effectiveDate, setEffectiveDate] = useState(todayISO());
  const [reason, setReason] = useState("repo_change");
  const [brokenPeriod, setBrokenPeriod] = useState(false);
  const [chosenId, setChosenId] = useState("");
  const chosenIdRef = useRef("");
  useEffect(() => { chosenIdRef.current = chosenId; }, [chosenId]);
  const [customEmi, setCustomEmi] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [history, setHistory] = useState([]);

  const base = useMemo(() => {
    if (!loan) return null;
    const bal = Number(loan.current_balance) || Number(loan.principal) || 0;
    const rate = Number(loan.interest_rate_pct) || 0;
    const rem = Number(loan.remaining_tenure_months) ||
      (Number(loan.tenure_years) || 0) * 12 || 0;
    let cur_emi = Number(loan.current_emi) || 0;
    // Fallback: compute EMI from (bal, rate, rem) when the user hasn't typed one
    if (cur_emi <= 0 && bal > 0 && rem > 0) {
      cur_emi = emiFn(bal, rate, rem);
    }
    return {
      name: loan.name,
      outstanding: bal,
      current_rate_pct: rate,
      current_emi: cur_emi,
      remaining_months: rem,
      benchmark_type: loan.benchmark_type || (loan.rate_type === "Fixed" ? "Fixed" : "Other"),
      rate_type: loan.rate_type,
      max_tenure_months: loan.max_tenure_months,
    };
  }, [loan]);

  // Keep "rate" and "bps" inputs in sync
  useEffect(() => {
    if (!base) return;
    if (mode === "rate") {
      if (newRate === "" || isNaN(Number(newRate))) {
        setDeltaBps(0);
      } else {
        setDeltaBps(Math.round((Number(newRate) - base.current_rate_pct) * 100));
      }
    } else {
      setNewRate((base.current_rate_pct + deltaBps / 100).toFixed(2));
    }
  }, [mode, newRate, deltaBps, base]);

  // Reset dialog state when re-opened for a new loan
  useEffect(() => {
    if (!open || !base) return;
    setNewRate(base.current_rate_pct.toFixed(2));
    setDeltaBps(0);
    setEffectiveDate(todayISO());
    setChosenId("");
    setCustomEmi("");
    setPreview(null);
    // auto-pick a sensible reason default based on benchmark
    const b = base.benchmark_type;
    if (b === "Repo-linked (EBLR)") setReason("repo_change");
    else if (b === "MCLR" || b === "Base Rate/BPLR") setReason("mclr_reset");
    else if (b === "Fixed") setReason("fixed_revision");
    else setReason("other");
  }, [open, base]);

  const fetchHistory = useCallback(async () => {
    if (!open || !base || !planName) return;
    try {
      const { data } = await api.get("/loans/rate-history", {
        params: { plan_name: planName, loan_name: base.name },
      });
      setHistory(data || []);
    } catch {
      /* ignore (401 for anon users) */
    }
  }, [open, base, planName]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Live preview — debounced
  useEffect(() => {
    if (!open || !base) return;
    const rate = Number(newRate);
    if (!isFinite(rate) || rate < 0 || rate > 36) { setPreview(null); return; }
    if (base.outstanding <= 0 || base.current_emi <= 0 || base.remaining_months <= 0) {
      setPreview(null); return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/loans/rate-change/compute", {
          loan: {
            name: base.name,
            outstanding: base.outstanding,
            current_rate_pct: base.current_rate_pct,
            current_emi: base.current_emi,
            remaining_months: base.remaining_months,
            benchmark_type: base.benchmark_type,
            rate_type: base.rate_type,
            max_tenure_months: base.max_tenure_months || null,
          },
          change: {
            new_rate_pct: rate,
            effective_date: effectiveDate,
            reason,
            broken_period: brokenPeriod,
            custom_emi: Number(customEmi) || null,
          },
        });
        if (!cancelled) {
          setPreview(data);
          if (!chosenIdRef.current) setChosenId(data.default_option);
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Preview failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, base, newRate, effectiveDate, reason, brokenPeriod, customEmi]);

  const chosen = useMemo(() => {
    if (!preview) return null;
    return (preview.options || []).find((o) => o.id === chosenId) || preview.chosen_option;
  }, [preview, chosenId]);

  const applyChange = async () => {
    if (!chosen || chosen.disabled) {
      toast.error("Please pick an option first");
      return;
    }
    if (!planName) {
      toast.error("Save the plan first before applying a rate change");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/loans/rate-change/apply", {
        plan_name: planName,
        loan_name: base.name,
        new_rate_pct: Number(newRate),
        effective_date: effectiveDate,
        reason,
        option_chosen: chosen.id,
        new_emi: chosen.new_emi,
        new_tenure_months: chosen.new_tenure_months,
        interest_remaining_before: preview.before.interest_from_now,
        interest_remaining_after: chosen.interest_from_now,
        broken_period: brokenPeriod,
      });
      toast.success(`Rate updated for ${base.name}`);
      await fetchHistory();
      onApplied?.({
        loan_name: base.name,
        new_rate: Number(newRate),
        new_emi: chosen.new_emi,
        new_tenure_months: chosen.new_tenure_months,
      });
      // keep dialog open so user can see the fresh history row
      void data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not apply change");
    } finally {
      setApplying(false);
    }
  };

  const undoLast = async () => {
    if (!window.confirm("Undo the latest rate change for this loan?")) return;
    try {
      await api.post("/loans/rate-change/undo", {
        plan_name: planName, loan_name: base.name,
      });
      toast.success("Rate change undone");
      await fetchHistory();
      onApplied?.({ loan_name: base.name, undone: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not undo");
    }
  };

  if (!open || !base) return null;
  const rateBps = preview?.rate_bps ?? 0;
  const rateUp = rateBps > 0;
  const rateDown = rateBps < 0;

  return (
    <div className="fixed inset-0 z-[10020] flex items-start sm:items-center justify-center p-2 sm:p-4" data-testid="rate-change-dialog">
      <button className="absolute inset-0 bg-black/70" onClick={onClose} aria-label="Close" />
      <div className="relative card-flat w-full max-w-4xl p-5 sm:p-6 max-h-[95vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
                data-testid="rate-change-close">
          <X size={16} weight="bold" />
        </button>

        <div className="eyebrow mb-1 flex items-center gap-2">
          <Percent size={14} weight="duotone" /> Update interest rate
        </div>
        <h2 className="font-serif text-2xl mb-1">{base.name}</h2>
        <p className="text-xs text-muted-foreground mb-4">
          Outstanding {formatINR(base.outstanding, true)} · Current rate {base.current_rate_pct.toFixed(2)}%
          · EMI ₹{Math.round(base.current_emi).toLocaleString("en-IN")} · {formatTenure(base.remaining_months)} left
          · {base.benchmark_type}
        </p>

        {/* Form row */}
        <div className="grid sm:grid-cols-5 gap-3 mb-4">
          <div className="sm:col-span-2">
            <div className="eyebrow mb-1 text-[9px]">New rate</div>
            <div className="flex gap-1 mb-1 text-[10px]">
              <button onClick={() => setMode("rate")}
                      className={`px-2 py-0.5 rounded border hairline ${mode === "rate" ? "bg-[hsl(var(--secondary))]/15 border-[hsl(var(--secondary))]" : ""}`}
                      data-testid="rate-mode-rate">Rate %</button>
              <button onClick={() => setMode("bps")}
                      className={`px-2 py-0.5 rounded border hairline ${mode === "bps" ? "bg-[hsl(var(--secondary))]/15 border-[hsl(var(--secondary))]" : ""}`}
                      data-testid="rate-mode-bps">± bps</button>
            </div>
            {mode === "rate" ? (
              <input type="number" step="0.05" value={newRate}
                     onChange={(e) => setNewRate(e.target.value)}
                     className="input-dark w-full px-2 py-1.5 text-sm"
                     data-testid="rate-input-new-rate" />
            ) : (
              <input type="number" step="25" value={deltaBps}
                     onChange={(e) => setDeltaBps(Number(e.target.value))}
                     className="input-dark w-full px-2 py-1.5 text-sm"
                     placeholder="±25, ±50, ±100"
                     data-testid="rate-input-bps" />
            )}
            <div className="text-[10px] text-muted-foreground mt-1">
              Old {base.current_rate_pct.toFixed(2)}% → {newRate || "–"}%
              {rateBps !== 0 && (
                <span className={`ml-2 ${rateUp ? "text-destructive" : rateDown ? "text-[hsl(var(--secondary))]" : ""}`}>
                  ({rateBps > 0 ? "+" : ""}{rateBps} bps)
                </span>
              )}
            </div>
          </div>

          <div>
            <div className="eyebrow mb-1 text-[9px]">Effective from</div>
            <input type="date" value={effectiveDate}
                   onChange={(e) => setEffectiveDate(e.target.value)}
                   className="input-dark w-full px-2 py-1.5 text-xs"
                   data-testid="rate-input-effective-date" />
          </div>

          <div className="sm:col-span-2">
            <div className="eyebrow mb-1 text-[9px]">Reason</div>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
                    className="input-dark w-full px-2 py-1.5 text-xs"
                    data-testid="rate-input-reason">
              {REASONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-[11px] text-muted-foreground mb-4 cursor-pointer">
          <input type="checkbox" checked={brokenPeriod}
                 onChange={(e) => setBrokenPeriod(e.target.checked)}
                 data-testid="rate-input-broken-period" />
          <span>Broken-period interest (first EMI uses old rate for days before and new rate for days after)</span>
        </label>

        {/* Preview */}
        {loading && !preview && (
          <div className="text-xs text-muted-foreground mb-3">Recalculating…</div>
        )}

        {preview && (
          <>
            {/* Option cards */}
            <div className="grid sm:grid-cols-3 gap-3 mb-4" data-testid="rate-options">
              {preview.options.map((o) => (
                <OptionCard key={o.id} opt={o}
                            chosen={chosenId === o.id}
                            isDefault={preview.default_option === o.id}
                            onPick={() => !o.disabled && setChosenId(o.id)}
                            customEmi={customEmi}
                            onCustomEmi={setCustomEmi} />
              ))}
            </div>

            {/* Headline + impact */}
            {chosen && !chosen.disabled && (
              <>
                <div className="card-flat p-4 mb-4" data-testid="rate-headline">
                  <div className="eyebrow mb-1 text-[9px] flex items-center gap-1">
                    {rateUp ? <TrendUp size={12} weight="duotone" className="text-destructive" />
                            : rateDown ? <TrendDown size={12} weight="duotone" className="text-[hsl(var(--secondary))]" /> : null}
                    Impact
                  </div>
                  <p className="text-sm leading-relaxed">{preview.headline}</p>
                </div>

                <ImpactGrid before={preview.before} chosen={chosen} rateBps={rateBps} oldRate={base.current_rate_pct}
                            newRate={Number(newRate)} />

                {/* Chart */}
                {preview.chart && preview.chart.length > 1 && (
                  <BalanceChart rows={preview.chart} oldRate={base.current_rate_pct} newRate={Number(newRate)} />
                )}

                {/* Nudges */}
                {rateUp && onPreparePrepayment && (
                  <button
                    onClick={() => onPreparePrepayment({
                      loan_name: base.name,
                      // Prefill with the extra interest cost under Keep EMI, as a prepayment anchor
                      amount: Math.round(((chosen.interest_from_now || 0) - preview.before.interest_from_now) / 2) || 100000,
                    })}
                    className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5 mt-3 mb-3"
                    data-testid="rate-nudge-prepay">
                    <Sparkle size={12} weight="duotone" />
                    Offset the rate increase — open Prepayment Simulator
                  </button>
                )}

                {(base.benchmark_type === "MCLR" || base.benchmark_type === "Base Rate/BPLR") && (
                  <div className="text-[11px] text-muted-foreground mb-3 flex items-start gap-2">
                    <WarningCircle size={12} weight="duotone" className="shrink-0 mt-0.5" />
                    Ask your bank about switching to a Repo-linked (EBLR) rate; compare switching fees first.
                  </div>
                )}

                <div className="text-[11px] text-muted-foreground border-t hairline pt-3 mb-4">
                  RBI rules on floating-rate EMI loans require banks, at a rate reset, to let you choose a
                  higher EMI, a longer tenure or a mix — and to prepay. Confirm fees with your lender.
                </div>
              </>
            )}
          </>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="mb-4" data-testid="rate-history-list">
            <div className="eyebrow mb-2 text-[9px] flex items-center gap-1">
              <Clock size={11} weight="duotone" /> Rate history
            </div>
            <div className="space-y-1 text-[11px]">
              {history.slice(0, 6).map((h) => (
                <div key={h.id} className={`flex justify-between border hairline p-2 ${h.undone ? "opacity-50 line-through" : ""}`}>
                  <span>
                    <b>{h.old_rate.toFixed(2)}% → {h.new_rate.toFixed(2)}%</b>
                    <span className="text-muted-foreground"> · {REASONS.find((r) => r.v === h.reason)?.label || h.reason}</span>
                  </span>
                  <span className="text-muted-foreground">
                    EMI ₹{h.emi_before.toLocaleString("en-IN")} → ₹{h.emi_after.toLocaleString("en-IN")} ·{" "}
                    {h.effective_date}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={applyChange} disabled={applying || !chosen || chosen.disabled || rateBps === 0}
                  className="btn-primary px-4 py-2 text-xs"
                  data-testid="rate-apply-btn">
            {applying ? "Applying…" : "Apply rate change"}
          </button>
          {history.some((h) => !h.undone) && (
            <button onClick={undoLast}
                    className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5"
                    data-testid="rate-undo-btn">
              <ArrowUDownLeft size={12} /> Undo last change
            </button>
          )}
          <div className="text-[10px] text-muted-foreground ml-auto">
            Estimates for planning only. Not financial advice. Confirm figures with your lender.
          </div>
        </div>
      </div>
    </div>
  );
}

function OptionCard({ opt, chosen, isDefault, onPick, customEmi, onCustomEmi }) {
  const disabled = !!opt.disabled;
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      className={`text-left p-3 border-2 rounded transition-colors ${
        chosen ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5"
               : "border-transparent card-flat hover:border-muted-foreground/30"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      data-testid={`rate-option-${opt.id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-medium">{opt.label}</div>
        {isDefault && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[hsl(var(--secondary))]/15 text-[hsl(var(--secondary))]">
            Recommended
          </span>
        )}
      </div>
      {disabled ? (
        <div className="text-[11px] text-destructive leading-snug">
          <WarningCircle size={11} weight="duotone" className="inline mr-1" />
          {opt.disabled_reason}
        </div>
      ) : (
        <>
          {opt.id === "custom" ? (
            <input type="number" value={customEmi} onChange={(e) => onCustomEmi(e.target.value)}
                   placeholder="Type EMI (₹)" className="input-dark w-full px-2 py-1 text-xs mb-1"
                   data-testid="rate-custom-emi" />
          ) : null}
          <div className="text-[11px] text-muted-foreground">EMI</div>
          <div className="num-metric text-sm">₹{(opt.new_emi || 0).toLocaleString("en-IN")}
            {typeof opt.emi_delta === "number" && opt.emi_delta !== 0 && (
              <span className={`text-[10px] ml-1 ${opt.emi_delta > 0 ? "text-destructive" : "text-[hsl(var(--secondary))]"}`}>
                ({opt.emi_delta > 0 ? "+" : ""}₹{Math.abs(opt.emi_delta).toLocaleString("en-IN")})
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">Tenure</div>
          <div className="num-metric text-sm">{formatTenure(opt.new_tenure_months)}
            {typeof opt.months_delta === "number" && opt.months_delta !== 0 && (
              <span className={`text-[10px] ml-1 ${opt.months_delta > 0 ? "text-destructive" : "text-[hsl(var(--secondary))]"}`}>
                ({opt.months_delta > 0 ? "+" : ""}{opt.months_delta}m)
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">Interest from now</div>
          <div className="num-metric text-xs">{formatINR(opt.interest_from_now, true)}</div>
          {(opt.warnings || []).map((w, i) => (
            <div key={`w-${opt.id}-${i}`} className="text-[10px] text-destructive mt-1">
              <WarningCircle size={10} weight="duotone" className="inline mr-0.5" />{w}
            </div>
          ))}
        </>
      )}
    </button>
  );
}

function ImpactGrid({ before, chosen, rateBps, oldRate, newRate }) {
  const int_delta = chosen.interest_from_now - before.interest_from_now;
  const emi_delta = (chosen.new_emi || 0) - before.emi;
  const months_delta = (chosen.new_tenure_months || 0) - before.remaining_months;
  const next_int_delta = (chosen.next_emi_interest || 0) - before.next_emi_interest;

  const closureDate = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + (chosen.new_tenure_months || 0));
    return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  })();
  const beforeClosure = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + before.remaining_months);
    return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  })();

  return (
    <div className="card-flat p-4 mb-3" data-testid="rate-impact-grid">
      <div className="eyebrow mb-2 text-[9px]">Before vs after · {chosen.label}</div>
      <div className="grid sm:grid-cols-2 gap-3 text-xs">
        <ImpactRow label="Interest rate" before={`${oldRate.toFixed(2)}%`} after={`${newRate.toFixed(2)}%`}
                   tone={rateBps < 0 ? "secondary" : rateBps > 0 ? "destructive" : "muted"} />
        <ImpactRow label="Monthly EMI" before={`₹${Math.round(before.emi).toLocaleString("en-IN")}`}
                   after={`₹${(chosen.new_emi || 0).toLocaleString("en-IN")}`}
                   delta={emi_delta} deltaFmt={(v) => `${v > 0 ? "+" : "−"}₹${Math.abs(v).toLocaleString("en-IN")}`}
                   deltaInvert />
        <ImpactRow label="Interest in next EMI" before={`₹${before.next_emi_interest.toLocaleString("en-IN")}`}
                   after={`₹${(chosen.next_emi_interest || 0).toLocaleString("en-IN")}`}
                   delta={next_int_delta}
                   deltaFmt={(v) => `${v > 0 ? "+" : "−"}₹${Math.abs(v).toLocaleString("en-IN")}`}
                   deltaInvert />
        <ImpactRow label="Remaining tenure" before={formatTenure(before.remaining_months)}
                   after={formatTenure(chosen.new_tenure_months || 0)}
                   delta={months_delta}
                   deltaFmt={(v) => `${v > 0 ? "+" : "−"}${Math.abs(v)} mths`}
                   deltaInvert />
        <ImpactRow label="Total interest from now" before={formatINR(before.interest_from_now, true)}
                   after={formatINR(chosen.interest_from_now, true)}
                   delta={int_delta}
                   deltaFmt={(v) => `${v > 0 ? "+" : "−"}${formatINR(Math.abs(v), true).replace("₹", "₹")}`}
                   deltaInvert />
        <ImpactRow label="Loan closure date" before={beforeClosure} after={closureDate} />
      </div>
    </div>
  );
}

function ImpactRow({ label, before, after, tone = "muted", delta, deltaFmt, deltaInvert }) {
  let deltaTone = "";
  if (typeof delta === "number" && delta !== 0) {
    const bad = deltaInvert ? delta > 0 : delta < 0;
    deltaTone = bad ? "text-destructive" : "text-[hsl(var(--secondary))]";
  }
  const toneCls = tone === "secondary" ? "text-[hsl(var(--secondary))]"
                 : tone === "destructive" ? "text-destructive" : "";
  return (
    <div className="flex items-center justify-between border-b hairline py-1.5 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">
        <span className="text-muted-foreground text-[11px]">{before} →</span>{" "}
        <span className={`font-medium ${toneCls}`}>{after}</span>
        {typeof delta === "number" && delta !== 0 && deltaFmt && (
          <div className={`text-[10px] ${deltaTone}`}>{deltaFmt(delta)}</div>
        )}
      </span>
    </div>
  );
}

function BalanceChart({ rows, oldRate, newRate }) {
  const maxBal = Math.max(...rows.map((r) => Math.max(r.old_balance, r.new_balance))) || 1;
  const maxMo = Math.max(...rows.map((r) => r.month)) || 1;
  const W = 480, H = 140, pad = 24;
  const px = (m) => pad + (W - 2 * pad) * (m / maxMo);
  const py = (b) => H - pad - (H - 2 * pad) * (b / maxBal);
  const line = (key) => rows.map((r, i) => `${i === 0 ? "M" : "L"} ${px(r.month).toFixed(1)} ${py(r[key]).toFixed(1)}`).join(" ");
  return (
    <div className="card-flat p-4 mb-3" data-testid="rate-balance-chart">
      <div className="eyebrow mb-2 text-[9px] flex items-center gap-1">
        <ChartLine size={11} weight="duotone" /> Outstanding balance over time
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32">
        <path d={line("old_balance")} fill="none" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.5" />
        <path d={line("new_balance")} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.75" />
      </svg>
      <div className="flex items-center gap-4 text-[10px] text-muted-foreground mt-1">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-0.5 bg-muted-foreground inline-block" /> Old @ {oldRate.toFixed(2)}%
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-0.5 bg-[hsl(var(--primary))] inline-block" /> New @ {newRate.toFixed(2)}%
        </span>
      </div>
    </div>
  );
}
